import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
  useContext,
} from 'react';
// Removed duplicate React import block
import Amplify, { Auth } from 'aws-amplify';
// import storage from './storage'; // Removed storage import
import { PageLoading } from './ui';
import * as Sentry from '@sentry/node';
import {
  identifyPosthogUser,
  resetPosthog,
} from './posthog';
import {
  identifyMixpanelUser,
  initMixpanel,
  resetMixpanel,
  consumeMixpanelOAuthSignupIfPending,
} from './mixpanel';
import { trackProductEvent } from './analytics';
import { ensureOneSignalUser } from './onesignal-events';

const isServer = typeof window === 'undefined';
const currentOrigin = !isServer
  ? window.location.origin
  : process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000';
const cookieDomain = !isServer
  ? window.location.hostname
  : process.env.NEXT_PUBLIC_COOKIE_DOMAIN || 'app.postdrop.io';

export const initializeAmplify = () =>
  Amplify.configure({
    Auth: {
      region: process.env.NEXT_PUBLIC_COGNITO_REGION,
      userPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID,
      identityPoolId: process.env.NEXT_PUBLIC_COGNITO_IDENTITY_POOL_ID,
      userPoolWebClientId: process.env.NEXT_PUBLIC_COGNITO_APP_CLIENT_ID,

      cookieStorage: {
        domain: cookieDomain,
        path: '/',
        expires: 365,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
      },

      oauth: {
        domain: (process.env.NEXT_PUBLIC_COGNITO_DOMAIN || '').replace(/^https?:\/\//, ''),
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: `${currentOrigin}/templates`,
        redirectSignOut: `${currentOrigin}/login`,
        responseType: 'code'
      }
    },
    ssr: true,
  });

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // initial loading
  const [refreshing, setRefreshing] = useState(true); // new flag for refresh
  // const credential = cookie.get('id_token') // Removed credential logic

  // Simplified setUserWrap - relies only on Amplify user object
  const setUserWrap = async (user) => {
    try {
      // init users
      if (!isServer && user) {
        Sentry.setUser({
          email: user?.attributes?.email,
          id: user?.attributes?.sub,
        });
      }
      if (!isServer && user) {
        initMixpanel();
        // Build traits object, only including defined values
        const posthogTraits: Record<string, any> = {};
        const mixpanelTraits: Record<string, any> = {};
        
        if (user?.attributes?.email) {
          posthogTraits.email = user.attributes.email;
          // Use $email for Mixpanel (reserved property for users index)
          mixpanelTraits.$email = user.attributes.email;
        }
        
        if (user?.attributes?.email_verified !== undefined) {
          posthogTraits.email_verified = user.attributes.email_verified;
          mixpanelTraits.email_verified = user.attributes.email_verified;
        }
        
        const fullName = user?.attributes?.['custom:full_name'];
        if (fullName) {
          posthogTraits.name = fullName;
          // Use $name for Mixpanel (reserved property for users index)
          mixpanelTraits.$name = fullName;
        }
        
        identifyPosthogUser(user?.attributes?.sub, posthogTraits);
        ensureOneSignalUser(user?.attributes?.sub);
        identifyMixpanelUser(user?.attributes?.sub, mixpanelTraits);

        if (consumeMixpanelOAuthSignupIfPending()) {
          trackProductEvent('User Signed Up', { method: 'google' });
          trackProductEvent('Account Verified', { method: 'google' });
          trackProductEvent('signup-google', { method: 'google' });
        }

        // Push to dataLayer for GTM (e.g. Hotjar user identification)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'userIdentified',
          userId: user?.attributes?.sub,
          userEmail: user?.attributes?.email || undefined,
          userName: user?.attributes?.['custom:full_name'] || undefined,
        });
      }
      // remove users
      if (!isServer && !user) {
        Sentry.configureScope((scope) => scope.setUser(null));
        // Note: We don't reset PostHog here to avoid clearing Cognito session tokens.
        // PostHog reset only happens during explicit logout.
      }
    } catch (_err) {
      console.error("Error in setUserWrap:", _err); // Added basic error logging
    }
    setUser(user);
  };

  const checkUser = async () => {
    try {
      setRefreshing(true);
      // Remove bypassCache to allow refresh tokens to work properly for session persistence
      // This allows Amplify to use cached tokens and only refresh when actually expired
      const currentUser = await Auth.currentAuthenticatedUser();
      await setUserWrap(currentUser);
      return currentUser;
    } catch {
      await setUserWrap(null);
      return null;
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  const login = async (cognitoUser: any) => {
    try {
      const session =
        cognitoUser?.signInUserSession || (await Auth.currentSession());
      if (session) {
        setUserWrap(cognitoUser);
        return cognitoUser;
      }
    } catch (err) {
      console.error("Error during login:", err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      if (!isServer) {
        resetPosthog();
        resetMixpanel();
      }
      await Auth.signOut({ global: true });
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  };

  const reload = async (forceReload = false) => {
    setLoading(true);
    return await checkUser();
  };

  const values = useMemo(
    () => ({ user, loading, refreshing, login, logout, reload }),
    [user, loading, refreshing]
  );

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      '`useAuth` hook must be used within a `AuthProvider` component'
    );
  }
  return context;
};

export const PageAuth = ({ enabled = false, children }) => {
  const { user, loading, refreshing } = useAuth();

  useEffect(() => {
    if (enabled && !loading && !refreshing && !user && !isServer) {
      window.location.href = '/login';
    }
  }, [enabled, loading, user, refreshing]);

  if (enabled && !user || (refreshing || loading)) {
    return (
      <>
        <PageLoading />
        <div className="invisible hidden">{children}</div>
      </>
    );
  }
  return children;
};
